import { useCallback, useEffect, useState } from "react";
import type { LevelSummary } from "@/domain/learning/model/level-summary";
import { ContentLoader } from "@/infrastructure/content/loader";

interface ManifestStateIdle {
  readonly status: "loading" | "error";
}
interface ManifestStateLoaded {
  readonly status: "loaded";
  readonly levels: readonly LevelSummary[];
}
type ManifestState = ManifestStateIdle | ManifestStateLoaded;

export interface UseManifestResult {
  readonly status: "loading" | "loaded" | "error";
  readonly levels: readonly LevelSummary[] | null;
  readonly retry: () => void;
}

/**
 * `manifest.json`（七段の見出しのみ）を取得する。取得失敗時はE-1として
 * `再試行`だけを提示できるよう、状態として公開する（`screen-flow.md` §8.1）。
 */
export function useManifest(): UseManifestResult {
  const [state, setState] = useState<ManifestState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attemptはretry()で再取得をトリガーするための意図的な依存
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    const loader = new ContentLoader();
    loader
      .loadManifest()
      .then((levels) => {
        if (!cancelled) {
          setState({ status: "loaded", levels });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: "error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    status: state.status,
    levels: state.status === "loaded" ? state.levels : null,
    retry,
  };
}
